import React, { useState } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Bilingual KPI / data tooltip.
 *
 * Phase 8.A QA-2 — meets the explicit requirements:
 *   • Desktop: hover + keyboard focus (Shadcn Tooltip).
 *   • Mobile: tap to toggle (Shadcn Popover) — pointer-coarse devices.
 *   • Reads the bilingual text from `useTranslation()` via the `text` prop
 *     (caller passes a t("...") string in the active locale).
 *   • Uses a 14px lucide-react `Info` icon — never an emoji.
 *   • Includes a stable `data-testid` so test automation can locate the
 *     trigger and the open tooltip body regardless of locale.
 *
 * Props:
 *   - text:    string — the resolved bilingual tooltip text.
 *   - label:   string — visually-hidden a11y label (defaults to "More info").
 *   - testId:  string — stable data-testid prefix (required).
 *   - side:    "top" | "right" | "bottom" | "left" (default "top").
 *   - className: optional extra classes for the trigger button.
 */
export default function InfoTooltip({
  text,
  label = "More info",
  testId,
  side = "top",
  className = "",
}) {
  // Detect coarse pointers (touch devices). useState so SSR / first-render
  // matches the client without flicker — recomputed on mount.
  const [isCoarse, setIsCoarse] = useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setIsCoarse(!!mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  if (!text) return null;

  const triggerBaseClasses =
    "inline-flex items-center justify-center align-middle text-muted-foreground/80 " +
    "hover:text-foreground focus-visible:text-foreground rounded-full " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3DEBFF] " +
    "focus-visible:ring-offset-1 focus-visible:ring-offset-background " +
    "transition-opacity transition-colors h-5 w-5";

  const trigger = (
    <button
      type="button"
      aria-label={label}
      data-testid={`${testId}-trigger`}
      className={`${triggerBaseClasses} ${className}`}
      // On coarse pointers we use Popover (click). On desktop the Tooltip
      // handles focus/hover natively, so no onClick needed there.
    >
      <Info className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );

  // Mobile / touch — tap to toggle (Popover).
  if (isCoarse) {
    return (
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          side={side}
          align="center"
          sideOffset={6}
          className="max-w-[260px] text-xs leading-relaxed"
          data-testid={`${testId}-content`}
        >
          {text}
        </PopoverContent>
      </Popover>
    );
  }

  // Desktop — hover + keyboard focus (Tooltip).
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side={side}
          align="center"
          sideOffset={6}
          className="max-w-[280px] text-xs leading-relaxed"
          data-testid={`${testId}-content`}
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
